<template>
    <BaseDialog v-model:open="isDialogOpen" show-close title="New connections from calendar">
        <div class="p-4 md:p-10 pt-0! flex flex-col gap-10">
            <h3 v-if="noAttendees" class="title-body text-valence-grey-750">There are no new calendar event attendees to add to your people.</h3>
            <div v-else-if="isSaved" class="text-center max-w-[250px]">
                <h3 class="title-body mb-2.5">People saved</h3>
                <p class="body-small text-[#595959] mb-5">You can provide additional context on your My People page. This allows me to deliver more tailored coaching.</p>
                <div class="flex flex-col items-center">
                    <BaseLink href="/people" class="base-btn-primary w-fit font-medium rounded-full" data-test-id="my-people-link">My People</BaseLink>
                    <BaseButton theme="tertiary" :class="{ 'font-medium': !tonalThemeEnabled }" @click="closeDialog">Dismiss</BaseButton>
                </div>
            </div>
            <template v-else>
                <h3 class="title-body"><i class="bi bi-people-fill" aria-hidden="true"></i> New people added</h3>
                <div class="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-5 max-w-[800px] max-h-[300px] overflow-auto">
                    <div v-for="attendee in attendees" :key="attendee.connection_id">
                        <ConnectionEditCard
                            :attendee="attendee"
                            @update:title="(title: string) => handleTitleUpdate(title, attendee.connection_id)"
                            @update:dismissed="(dismissed: boolean) => handleDismissedUpdate(dismissed, attendee.connection_id)" />
                    </div>
                </div>
                <div class="flex justify-end gap-6">
                    <BasePopover content-class="border-none" side="top" :side-offset="18">
                        <template #trigger>
                            <button aria-label="About people" class="bi bi-info-circle text-valence-primary-500 text-lg/none"></button>
                        </template>
                        <template #content>
                            <div class="body-small max-w-[260px] bg-[#E0E9FF] p-3 rounded-lg shadow-xl">
                                <p class="font-semibold">About people</p>
                                <p class="font-normal">
                                    People are memories of individuals you work with, to help enhance the quality of your coaching.
                                    <span class="font-semibold">They are private to you</span>, and information won't be shared with others.
                                </p>
                            </div>
                        </template>
                    </BasePopover>
                    <BaseButton
                        data-test-id="save-details-btn"
                        theme="primary"
                        :size="tonalThemeEnabled ? 'sm' : undefined"
                        class="justify-center flex items-center gap-2"
                        :class="tonalThemeEnabled ? 'w-50' : 'body-small w-[200px]'"
                        :disabled="saveDetailsBtnDisabled"
                        @click="handleSaveDetails">
                        <LoadingSpinner v-if="isFetching" class="w-4 h-4" />{{ isFetching ? "Saving" : "Save" }} Details
                    </BaseButton>
                </div>
            </template>
        </div>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="errorMessage" data-test-id="save-details-error-toast"></BaseToast>
    </BaseDialog>
</template>

<script setup lang="ts">
type BulkUpdatePayloadItem = { connection_id: string; dismissed?: boolean; job_title?: string };

import { useFetch } from "~vue/api";
import ConnectionEditCard from "~vue/Connections/ConnectionEditCard.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { ConnectionCalendarAttendee } from "~vue/types/connection";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, onUnmounted, ref, watch } from "vue";

import BaseButton from "../BaseButton.vue";
import BaseDialog from "../BaseDialog.vue";
import BasePopover from "../BasePopover.vue";
import BaseToast from "../BaseToast.vue";

interface Props {
    attendees?: ConnectionCalendarAttendee[];
}

const props = withDefaults(defineProps<Props>(), {
    attendees: () => [],
});

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const isDialogOpen = defineModel<boolean>("open", { default: false });

const noAttendees = computed(() => props.attendees.length === 0);
const saveDetailsBtnDisabled = computed(() => isFetching.value || bulkUpdatePayload.value.people.length === 0);

const isErrorToastOpen = ref(false);
const bulkUpdatePayload = ref<{ people: BulkUpdatePayloadItem[] }>({ people: [] });
const errorMessage = ref("Saving people detail has failed, please try again.");

const handleTitleUpdate = (title: string, connectionId: string) => {
    const connection = bulkUpdatePayload.value.people.find((c) => c.connection_id === connectionId);
    if (connection) {
        connection.job_title = title;
    } else {
        bulkUpdatePayload.value.people.push({ connection_id: connectionId, job_title: title });
    }
};

const handleDismissedUpdate = (dismissed: boolean, connectionId: string) => {
    const userInteraction = dismissed ? "connection_dismissed" : "connection_dismissed_undone";
    logUserInteraction(userInteraction, { source: "calendar_integration", connection_id: connectionId });

    const connection = bulkUpdatePayload.value.people.find((c) => c.connection_id === connectionId);
    if (connection) {
        connection["dismissed"] = dismissed;
    } else {
        bulkUpdatePayload.value.people.push({ connection_id: connectionId, dismissed });
    }
};
const { error: bulkUpdateError, execute: bulkUpdate, onFetchError, onFetchResponse, isFetching } = useFetch("/api/connections/bulk/", { immediate: false }).put(bulkUpdatePayload);

onFetchError(() => {
    isErrorToastOpen.value = !isSessionEndedError(bulkUpdateError.value);
});

onFetchResponse(() => {
    isSaved.value = true;
});

const isSaved = ref(false);

const handleSaveDetails = async () => {
    if (bulkUpdatePayload.value.people.length > 0) {
        await bulkUpdate();
    }
};

const closeDialog = () => {
    isDialogOpen.value = false;
};

watch(isDialogOpen, (newVal) => {
    if (!newVal) {
        isSaved.value = false;
        bulkUpdatePayload.value = { people: [] };
    }
});

onUnmounted(() => {
    isDialogOpen.value = false;
});

defineExpose({
    // Exposed for unit testing
    bulkUpdatePayload,
});
</script>
